const EDITABLE_FIELDS = ['hoursWorked', 'publicComments']
const WEEKDAYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY']

function editableValue(entry, field) {
  if (field === 'publicComments') return entry?.[field] ?? ''
  const value = entry?.[field] ?? 0
  return value === '' ? '' : Number(value)
}

function entryChanges(baseline, entries) {
  const original = new Map(baseline.map(entry => [entry.date, entry]))
  return entries.flatMap(entry => EDITABLE_FIELDS.filter(field =>
    !Object.is(editableValue(entry, field), editableValue(original.get(entry.date), field))
  ).map(field => ({ date: entry.date, field, value: editableValue(entry, field) })))
}

function validateEntries(entries, monthId) {
  const prefix = `${monthId.slice(0, 4)}-${monthId.slice(4, 6)}-`
  const seen = new Set()
  for (const entry of entries) {
    const date = new Date(`${entry.date}T12:00:00Z`)
    if (!entry.date?.startsWith(prefix) || !Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== entry.date || seen.has(entry.date)) {
      throw new Error('The timesheet contains an invalid or duplicate date. Reload it before saving.')
    }
    seen.add(entry.date)
    const hours = editableValue(entry, 'hoursWorked')
    if (hours === '' || !Number.isFinite(hours) || hours < 0 || hours > 24) {
      throw new Error(`Enter hours between 0 and 24 for ${entry.date}.`)
    }
    if (typeof editableValue(entry, 'publicComments') !== 'string') throw new Error('Comments must be text.')
  }
}

function mergeEdits(latest, baseline, edited, monthId) {
  validateEntries(edited, monthId)
  const original = new Map(baseline.map(entry => [entry.date, entry]))
  const result = latest.map(entry => ({ ...entry }))
  const remote = new Map(result.map(entry => [entry.date, entry]))
  for (const change of entryChanges(baseline, edited)) {
    const entry = remote.get(change.date)
    const before = editableValue(original.get(change.date), change.field)
    const current = editableValue(entry, change.field)
    if (!entry || (!Object.is(current, before) && !Object.is(current, change.value))) {
      const error = new Error(`${change.date} was changed elsewhere. Your edits are kept here; reload this month to review the saved version before trying again.`)
      error.code = 'timesheet/conflict'
      throw error
    }
    entry[change.field] = change.value
    entry.status = 'UPDATED'
  }
  return result
}

function totalHours(entries) {
  return Math.round(entries.reduce((sum, entry) => sum + (Number(entry.hoursWorked) || 0), 0) * 1000000) / 1000000
}

function defaultTimesheet(project, monthId, holidays = []) {
  const year = Number(monthId.slice(0, 4))
  const month = Number(monthId.slice(4, 6))
  if (!/^\d{6}$/.test(monthId) || month < 1 || month > 12 || year < 1900 || year > 9999) throw new Error('Choose a valid month.')
  const details = project.projectDetails || {}
  const workingDays = (details.workDays || []).map(day => day.toUpperCase())
  const overrides = Object.fromEntries(Object.entries(details.workDayHoursOverride || {}).map(([day, hours]) => [day.toUpperCase(), hours]))
  const holidayNames = new Map(holidays.map(holiday => [holiday.date, holiday.name]))
  const entries = []
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate()
  for (let day = 1; day <= lastDay; day++) {
    const date = `${monthId.slice(0, 4)}-${monthId.slice(4, 6)}-${String(day).padStart(2, '0')}`
    const weekday = WEEKDAYS[new Date(Date.UTC(year, month - 1, day)).getUTCDay()]
    const holiday = holidayNames.get(date)
    const hoursWorked = !holiday && workingDays.includes(weekday)
      ? Number(overrides[weekday] ?? details.workDayHours ?? 8) : 0
    entries.push({ date, weekday, hoursWorked, publicComments: holiday || '', status: 'DEFAULT' })
  }
  return { startDate: entries[0].date, endDate: entries[entries.length - 1].date, entries, totalHours: totalHours(entries) }
}

module.exports = { editableValue, entryChanges, validateEntries, mergeEdits, totalHours, defaultTimesheet }
